import React, { useState, useEffect } from 'react'
import Modal from '../components/Modal'
import Button from '../components/Button'
import Icon from '../components/Icon'
import { useAccommodationEventsStore } from '../../stores/accommodationEventsStore'
import { Student, Accommodation, AccommodationStatus } from '../../types'

interface LogAccommodationProps {
  isOpen: boolean
  onClose: () => void
  student: Student
  classId: string
}

const LogAccommodation: React.FC<LogAccommodationProps> = ({
  isOpen,
  onClose,
  student,
  classId,
}) => {
  const {
    accommodationEvents,
    loading,
    error,
    isCreating,
    fetchAccommodationEvents,
    saveAccommodationEvents,
    clearError,
  } = useAccommodationEventsStore()

  const [accommodationStatuses, setAccommodationStatuses] = useState<
    Record<string, AccommodationStatus>
  >({})
  const [notes, setNotes] = useState<string>('')
  const [targetDate, setTargetDate] = useState(new Date()) // Make this mutable

  // Reset target date to today when modal opens
  useEffect(() => {
    if (isOpen) {
      setTargetDate(new Date())
      setNotes('') // Reset notes when modal opens
    }
  }, [isOpen])

  // Fetch existing accommodation events when modal opens or date changes
  useEffect(() => {
    if (isOpen && student) {
      // Clear previous state when date changes
      setAccommodationStatuses({})
      setNotes('') // Clear notes when date changes
      // Always fetch fresh data
      fetchAccommodationEvents(student._id, classId, targetDate)
    }
  }, [isOpen, student, classId, targetDate, fetchAccommodationEvents])

  // Initialize accommodation statuses when events are loaded or student changes
  useEffect(() => {
    if (student?.accommodations && isOpen) {
      const initialStatuses: Record<string, AccommodationStatus> = {}

      // Initialize all student accommodations with N/A
      student.accommodations.forEach((accommodation: Accommodation) => {
        initialStatuses[accommodation] = AccommodationStatus.N_A
      })

      // Override with existing event statuses for the selected date
      if (accommodationEvents && accommodationEvents.length > 0) {
        // Filter events for this specific student, class, and date
        const targetDateString = targetDate.toDateString()
        const relevantEvents = accommodationEvents.filter((event: any) => {
          const eventDate = new Date(event.date).toDateString()
          const isMatchingStudent =
            event.student?._id === student._id || event.student === student._id
          const isMatchingClass =
            event.class?._id === classId || event.class === classId

          return (
            isMatchingStudent &&
            isMatchingClass &&
            eventDate === targetDateString
          )
        })

        relevantEvents.forEach((event: any) => {
          if (
            student.accommodations.includes(
              event.accommodation as Accommodation,
            )
          ) {
            initialStatuses[event.accommodation] =
              event.status as AccommodationStatus
          }
        })

        // Set notes from the first relevant event (they should all have the same notes for a given date)
        if (relevantEvents.length > 0 && relevantEvents[0].notes) {
          setNotes(relevantEvents[0].notes)
        }
      }

      setAccommodationStatuses(initialStatuses)
    }
  }, [student, accommodationEvents, classId, targetDate, isOpen])

  const handleDateChange = (newDate: Date) => {
    // Create date at noon UTC to avoid timezone issues
    const dateAtNoonUTC = new Date(
      newDate.getFullYear(),
      newDate.getMonth(),
      newDate.getDate(),
    )
    dateAtNoonUTC.setUTCHours(12, 0, 0, 0)
    setTargetDate(dateAtNoonUTC)
  }

  const handleStatusChange = (
    accommodation: string,
    status: AccommodationStatus,
  ) => {
    setAccommodationStatuses((prev) => ({
      ...prev,
      [accommodation]: status,
    }))
  }

  const handleSave = async () => {
    try {
      await saveAccommodationEvents({
        studentId: student._id,
        classId,
        date: targetDate,
        accommodationStatuses,
        notes,
      })

      // Immediately re-fetch the data to ensure we have the latest state
      await fetchAccommodationEvents(student._id, classId, targetDate)

      onClose()
    } catch (error) {
      console.error('Error saving accommodation events:', error)
    }
  }

  const handleCancel = () => {
    clearError()
    setAccommodationStatuses({}) // Reset state when closing
    setNotes('') // Reset notes when closing
    setTargetDate(new Date()) // Reset to today
    onClose()
  }

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const formatDateForInput = (date: Date) => {
    // Use local date values to avoid timezone conversion
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const getStatusColor = (status: AccommodationStatus) => {
    switch (status) {
      case AccommodationStatus.PROVIDED:
        return 'bg-green-100 text-green-800'
      case AccommodationStatus.DECLINED:
        return 'bg-red-100 text-red-800'
      case AccommodationStatus.NOT_NEEDED:
        return 'bg-blue-100 text-blue-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  // Check if there are existing events for the selected date
  const hasExistingEvents =
    accommodationEvents &&
    accommodationEvents.length > 0 &&
    accommodationEvents.some((event: any) => {
      const eventDate = new Date(event.date).toDateString()
      const targetDateString = targetDate.toDateString()
      const isMatchingStudent =
        event.student?._id === student._id || event.student === student._id
      const isMatchingClass =
        event.class?._id === classId || event.class === classId
      return (
        isMatchingStudent && isMatchingClass && eventDate === targetDateString
      )
    })

  if (!student) return null

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      title="Log Accommodations"
      size="lg"
    >
      <div className="space-y-6">
        {/* Header Information with Date Picker */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700">Student:</span>
              <div className="mt-1 text-gray-900">
                {student.firstName} {student.lastName}
              </div>
            </div>
            <div>
              <span className="font-medium text-gray-700">Date:</span>
              <div className="mt-1">
                <input
                  type="date"
                  value={formatDateForInput(targetDate)}
                  onChange={(e) => {
                    const inputDate = new Date(e.target.value + 'T00:00:00') // Parse as local date
                    handleDateChange(inputDate)
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={loading || isCreating}
                />
              </div>
              <div className="mt-1 text-xs text-gray-500">
                {formatDate(targetDate)}
              </div>
            </div>
          </div>

          {/* Status indicator */}
          <div className="mt-3 pt-3 border-t border-gray-200">
            <div className="flex items-center text-sm">
              <div
                className={`w-2 h-2 rounded-full mr-2 ${hasExistingEvents ? 'bg-green-400' : 'bg-gray-400'}`}
              ></div>
              <span className="text-gray-600">
                {hasExistingEvents
                  ? 'Events exist for this date'
                  : 'No events found for this date - creating new'}
              </span>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">
              Loading accommodations...
            </span>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Accommodations List */}
        {!loading &&
          student.accommodations &&
          student.accommodations.length > 0 && (
            <div className="space-y-4">
              {/* Notes Section */}
              <div className="space-y-2 mt-6">
                <label
                  htmlFor="notes"
                  className="block text-sm font-medium text-gray-700"
                >
                  Notes (Optional)
                </label>
                <textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  disabled={loading || isCreating}
                  placeholder="Add any additional notes about today's accommodations..."
                  rows={4}
                  maxLength={1000}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed resize-none"
                />
                <div className="text-xs text-gray-500 text-right">
                  {notes.length}/1000 characters
                </div>
              </div>

              <h3 className="text-lg font-medium text-gray-900">
                Student Accommodations ({student.accommodations.length})
              </h3>

              <div className="space-y-3">
                {student.accommodations.map((accommodation: Accommodation) => (
                  <div
                    key={accommodation}
                    className="border border-gray-200 rounded-lg p-4"
                  >
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start space-y-3 sm:space-y-0 sm:space-x-4">
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-gray-900 break-words">
                          {accommodation}
                        </h4>
                      </div>

                      <div className="flex-shrink-0 w-full sm:w-auto">
                        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
                          {Object.values(AccommodationStatus).map(
                            (status: string) => {
                              const isSelected =
                                (accommodationStatuses[accommodation] ||
                                  AccommodationStatus.N_A) === status
                              return (
                                <Button
                                  key={status}
                                  type="button"
                                  onClick={() =>
                                    handleStatusChange(
                                      accommodation,
                                      status as AccommodationStatus,
                                    )
                                  }
                                  disabled={loading || isCreating}
                                  variant="ghost"
                                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                                    isSelected
                                      ? `${getStatusColor(status as AccommodationStatus)} ring-2 ring-offset-1 ${
                                          status ===
                                          AccommodationStatus.PROVIDED
                                            ? 'ring-green-400'
                                            : status ===
                                                AccommodationStatus.DECLINED
                                              ? 'ring-red-400'
                                              : status ===
                                                  AccommodationStatus.NOT_NEEDED
                                                ? 'ring-blue-400'
                                                : 'ring-gray-400'
                                        }`
                                      : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
                                  }`}
                                >
                                  {status}
                                </Button>
                              )
                            },
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

        {/* No Accommodations */}
        {!loading &&
          (!student.accommodations || student.accommodations.length === 0) && (
            <div className="text-center py-8">
              <div className="text-gray-400 mb-2">
                <Icon type="document-text" className="mx-auto h-12 w-12" strokeWidth={1} />
              </div>
              <h3 className="text-sm font-medium text-gray-900 mb-1">
                No Accommodations
              </h3>
              <p className="text-sm text-gray-500">
                This student has no accommodations assigned.
              </p>
            </div>
          )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t border-gray-200">
          <Button
            onClick={handleCancel}
            disabled={isCreating}
            variant="secondary"
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>

          {student.accommodations && student.accommodations.length > 0 && (
            <Button
              onClick={handleSave}
              disabled={isCreating || loading}
              variant="primary"
              loading={isCreating}
              className="w-full sm:w-auto"
            >
              {hasExistingEvents ? 'Update' : 'Save'}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default LogAccommodation
